/* =========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* =========================================================================== */

/*
 * TagExportPng.jsx — ส่งออกป้ายราคาเป็น PNG   (งานข้อ 5 · Step 7.7)
 *   ดึงข้อมูล + รูป base64 จาก GET /tag_export/<id> แล้ววาดป้ายลง canvas ในเบราว์เซอร์
 *   ทำไมไม่ทำฝั่ง server: mPDF ออกได้แต่ PDF · GD วาดวรรณยุกต์ไทยผิด · Imagick บนเครื่องลูกค้าไม่แน่ว่ามี
 *   browser วาดไทยถูกอยู่แล้ว (ฟอนต์ Sarabun ที่หน้าเว็บโหลด) และรูปมากับ /api เดียวกัน ไม่ติด CORS
 *
 *   2 โหมด: ทีละใบ 70x50 มม. 300 dpi (ส่งไลน์/เครื่องพิมพ์สติกเกอร์) · แผ่น A4 เรียงตามแม่แบบ 200 dpi
 *   ได้หลายไฟล์ = รวมเป็น .zip เดียว (เดิมโหลดทีละไฟล์ Chrome ถามอนุญาต · 18 ต.ค. 2569) · ไฟล์เดียว = PNG ตรง ๆ
 *   หน้าตาป้ายล้อจาก print/tag.print.html (หัวแดง โลโก้ ชื่อ · รูป+QR ซ้าย · ราคาขวา 1-3 แถว)
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';

import { FetchData } from 'services/Request';
import zipStore from 'layouts/_entrio/zipStore';

const RED = '#ff0000';
const YELLOW = '#ffff00';
const BLUE = '#0000ff';
const FONT = '"Sarabun", "Tahoma", sans-serif';

const num = (v) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/* วาดรูปแบบ contain ในกรอบสี่เหลี่ยม */
const drawContain = (ctx, img, x, y, size) => {
  if (!img) return;
  const s = Math.min(size / img.width, size / img.height);
  const w = img.width * s;
  const h = img.height * s;
  ctx.drawImage(img, x + (size - w) / 2, y + (size - h) / 2, w, h);
};

/* ภาษาไทยไม่เว้นวรรคระหว่างคำ ของเดิมตัดได้แค่ที่ช่องว่าง ชื่อยาวจึงวาดบรรทัดเดียวล้นป้าย (16 ต.ค. 2569)
   หน่วยตัดบรรทัด: คำตามช่องว่าง → คำไทยด้วย Intl.Segmenter (ถ้ามี) → ทีละตัวอักษร (สระบน/ล่าง วรรณยุกต์ติดพยัญชนะ) */
const chars = (text) => String(text).match(/[\s\S][\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]*/gu) || [];
const segmenter =
  typeof Intl !== 'undefined' && Intl.Segmenter
    ? new Intl.Segmenter('th', { granularity: 'word' })
    : null;
const toUnits = (text, fine) =>
  String(text || '')
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((w, i) => {
      const parts = !fine
        ? [w]
        : segmenter
        ? Array.from(segmenter.segment(w), (x) => x.segment)
        : chars(w);
      return parts.map((t, j) => ({ text: t, space: i > 0 && j === 0 }));
    });

/* วางหน่วยลงบรรทัดแบบ greedy · splitWide = หน่วยที่กว้างเกินบรรทัดถูกหั่นเป็นตัวอักษร */
const wrap = (ctx, units, maxWidth, splitWide) => {
  const lines = [];
  let cur = '';
  const put = (text, space) => {
    const test = cur ? `${cur}${space ? ' ' : ''}${text}` : text;
    if (!cur || ctx.measureText(test).width <= maxWidth) {
      cur = test;
    } else {
      lines.push(cur);
      cur = text;
    }
  };
  units.forEach(({ text, space }) => {
    if (splitWide && ctx.measureText(text).width > maxWidth) {
      chars(text).forEach((c, i) => put(c, space && i === 0));
    } else {
      put(text, space);
    }
  });
  if (cur) lines.push(cur);
  return lines;
};

/* ตัดชื่อเป็นบรรทัดให้พอดีความกว้าง ลดขนาดฟอนต์ลงจนลงไม่เกิน maxLines (คงชื่อเต็ม เหมือน fitFontSize ใน PHP)
   ยาวเกินแม้ตัวเล็กสุด = ตัดบรรทัดสุดท้ายด้วย … ไม่ให้เกิน maxLines (16 ต.ค. 2569) */
const fitLines = (ctx, text, maxWidth, maxLines, maxPx, minPx) => {
  const coarse = toUnits(text, false);
  const fine = toUnits(text, true);
  for (let px = maxPx; px >= minPx; px -= 1) {
    ctx.font = `bold ${px}px ${FONT}`;
    const byWord = wrap(ctx, coarse, maxWidth, false);
    if (byWord.length <= maxLines && byWord.every((l) => ctx.measureText(l).width <= maxWidth)) {
      return { lines: byWord, px };
    }
    const bySegment = wrap(ctx, fine, maxWidth, true);
    if (bySegment.length <= maxLines) return { lines: bySegment, px };
  }
  ctx.font = `bold ${minPx}px ${FONT}`;
  const all = wrap(ctx, fine, maxWidth, true);
  const lines = all.slice(0, maxLines);
  if (all.length > maxLines) {
    const last = chars(lines[maxLines - 1]);
    while (last.length && ctx.measureText(`${last.join('')}…`).width > maxWidth) last.pop();
    lines[maxLines - 1] = `${last.join('').trimEnd()}…`;
  }
  return { lines, px: minPx };
};

const pill = (ctx, x, y, w, h, text, fill, stroke, color) => {
  roundRect(ctx, x, y, w, h, h / 2);
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  ctx.lineWidth = Math.max(1, h * 0.05);
  ctx.strokeStyle = stroke;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.font = `bold ${h * 0.58}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + w / 2, y + h / 2 + h * 0.03);
};

/* วาดป้าย 1 ใบลง ctx ที่ตำแหน่ง x,y ขนาด w,h (px) — สัดส่วนล้อจาก tag.print.html */
const drawLabel = (ctx, x, y, w, h, item, imgs) => {
  ctx.save();
  ctx.translate(x, y);
  const r = h * 0.035;
  const pad = h * 0.035;
  const headerH = h * 0.27;

  ctx.fillStyle = '#ffffff';
  roundRect(ctx, 0, 0, w, h, r);
  ctx.fill();

  ctx.save();
  roundRect(ctx, 0, 0, w, h, r);
  ctx.clip();
  ctx.fillStyle = RED;
  ctx.fillRect(0, 0, w, headerH);
  ctx.restore();

  const logoS = headerH - pad * 2;
  drawContain(ctx, imgs.logo, pad, pad, logoS);

  const nameX = pad * 2 + logoS;
  const nameW = w - nameX - pad;
  const { lines, px } = fitLines(ctx, item.name, nameW, 2, h * 0.105, h * 0.065);
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${px}px ${FONT}`;
  const lineH = px * 1.12;
  const startY = headerH / 2 - ((lines.length - 1) * lineH) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(nameX, 0, nameW, headerH); // ชื่อห้ามล้นกล่องหัวป้าย (16 ต.ค. 2569)
  ctx.clip();
  lines.forEach((l, i) => ctx.fillText(l, nameX + nameW / 2, startY + i * lineH));
  ctx.restore();

  const bodyY = headerH + pad;
  const bodyH = h - headerH - pad * 2;
  const colW = w * 0.25;
  const imgS = Math.min(colW - pad, bodyH * 0.48);
  drawContain(ctx, imgs.image, pad, bodyY, imgS);
  drawContain(ctx, imgs.qrcode, pad, bodyY + imgS + pad, imgS);

  const px0 = colW + pad;
  const pw = w - colW - pad * 2;
  const lns = item.lines || [];
  if (lns.length <= 1) {
    const price = lns[0] ? num(lns[0].price) : '';
    ctx.fillStyle = RED;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let size = h * 0.4;
    ctx.font = `bold ${size}px ${FONT}`;
    while (ctx.measureText(`${price}.-`).width > pw && size > h * 0.15) {
      size -= 2;
      ctx.font = `bold ${size}px ${FONT}`;
    }
    ctx.fillText(price, px0 + pw / 2 - size * 0.18, bodyY + bodyH / 2);
    ctx.font = `bold ${size * 0.3}px ${FONT}`;
    ctx.fillText(
      '.-',
      px0 + pw / 2 + ctx.measureText(price).width * 1.6 + size * 0.1,
      bodyY + bodyH / 2 + size * 0.3
    );
  } else if (lns.length === 2) {
    const ph = bodyH * 0.42;
    const gap = bodyH * 0.1;
    const y1 = bodyY + (bodyH - ph * 2 - gap) / 2;
    pill(ctx, px0, y1, pw, ph, `${num(lns[0].quantity)} = ${num(lns[0].price)}.-`, null, RED, RED);
    const d = lns[1].type === 'D';
    pill(
      ctx,
      px0,
      y1 + ph + gap,
      pw,
      ph,
      `${num(lns[1].quantity)} = ${num(lns[1].price)}.-`,
      d ? RED : YELLOW,
      d ? RED : YELLOW,
      d ? YELLOW : BLUE
    );
  } else {
    const ph = bodyH * 0.29;
    const gap = bodyH * 0.06;
    const y1 = bodyY + (bodyH - ph * 3 - gap * 2) / 2;
    pill(ctx, px0, y1, pw, ph, `${num(lns[0].quantity)} = ${num(lns[0].price)}.-`, null, RED, RED);
    const d2 = lns[1].type === 'D';
    pill(
      ctx,
      px0,
      y1 + ph + gap,
      pw,
      ph,
      `${num(lns[1].quantity)} = ${num(lns[1].price)}.-`,
      d2 ? RED : null,
      RED,
      d2 ? YELLOW : BLUE
    );
    const d3 = lns[2].type === 'D';
    pill(
      ctx,
      px0,
      y1 + (ph + gap) * 2,
      pw,
      ph,
      `${num(lns[2].quantity)} = ${num(lns[2].price)}.-`,
      d3 ? RED : YELLOW,
      d3 ? RED : YELLOW,
      d3 ? YELLOW : BLUE
    );
  }

  ctx.lineWidth = Math.max(2, h * 0.012);
  ctx.strokeStyle = RED;
  roundRect(ctx, ctx.lineWidth / 2, ctx.lineWidth / 2, w - ctx.lineWidth, h - ctx.lineWidth, r);
  ctx.stroke();
  ctx.restore();
};

const download = (blob, name) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};

const toBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

/* ชื่อไฟล์ใน zip — ตัดอักขระที่ใช้เป็นชื่อไฟล์ไม่ได้ (/ จะกลายเป็นโฟลเดอร์) และกันชื่อซ้ำ (SKU ซ้ำในป้ายเก่า) */
const uniqueName = (name, used) => {
  const base = String(name).replace(/[\\/:*?"<>|]/g, '_');
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : '';
  let out = base;
  for (let n = 2; used.has(out); n += 1) out = `${stem}_${n}${ext}`;
  used.add(out);
  return out;
};

function TagExportPng({ tagId, code, app, onNotify, confirmExpired, disabled, deniedText }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [imgs, setImgs] = useState([]);
  const [preview, setPreview] = useState('');
  const [progress, setProgress] = useState(-1);
  const [step, setStep] = useState('');
  const [busy, setBusy] = useState(false);

  const labelMm = () => {
    const t = data && data.template;
    return t && t.paper === 'SINGLE' ? { w: t.width_mm, h: t.height_mm } : { w: 70, h: 50 };
  };

  const load = async () => {
    /* ข้อ 6 — ป้ายจากแคมเปญนอกช่วงวันที่ ส่งออกได้เฉพาะผู้มีสิทธิ์ campaign_expired ที่กดยืนยัน
       GET ส่ง body ไม่ได้ (เบราว์เซอร์ทิ้ง) จึงส่ง ?expired=Y ใน URL (16 ต.ค. 2569) */
    const expired = await confirmExpired();
    if (!expired) return;
    setOpen(true);
    setBusy(true);
    setPreview('');
    try {
      const result = await FetchData(
        'GET',
        `/tag_export/${tagId}${expired === 'Y' ? '?expired=Y' : ''}`,
        app.token,
        { lang: app.lang }
      );
      if (result.status !== 'ok') {
        setOpen(false);
        onNotify(
          result.msg === 'campaign_expired'
            ? `ส่งออกไม่ได้ ${deniedText || 'แคมเปญของป้ายนี้อยู่นอกช่วงวันที่'}`
            : 'โหลดข้อมูลป้ายไม่ได้',
          'error'
        );
        return;
      }
      /* ใส่ข้อความไทยด้วย ไม่งั้นเบราว์เซอร์โหลดแค่ชุดอักษรละติน แล้ววาดไทยด้วยฟอนต์สำรอง (16 ต.ค. 2569) */
      await document.fonts.load(`bold 20px ${FONT}`, 'กิ่นั้น ABC 0123456789.-').catch(() => null);
      const logo = await loadImage(result.logo);
      const loaded = await Promise.all(
        result.items.map(async (it) => ({
          logo,
          image: await loadImage(it.image),
          qrcode: await loadImage(it.qrcode),
        }))
      );
      setData(result);
      setImgs(loaded);
      if (result.items.length) {
        const c = document.createElement('canvas');
        c.width = 560;
        c.height = 400;
        drawLabel(c.getContext('2d'), 0, 0, 560, 400, result.items[0], loaded[0]);
        setPreview(c.toDataURL('image/png'));
      }
    } catch (err) {
      /* เน็ตหลุด/เซิร์ฟเวอร์ไม่ตอบ — ปิดหน้าต่างที่ค้าง "กำลังโหลด" แล้วแจ้ง (16 ต.ค. 2569) */
      setOpen(false);
      onNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
    } finally {
      setBusy(false);
    }
  };

  /* วาดทีละไฟล์ด้วย draw(i) → canvas · 1 ไฟล์โหลดเป็น PNG · หลายไฟล์รวมเป็น zip เดียว */
  const save = async (count, draw, fileName, zipName) => {
    setBusy(true);
    try {
      if (count === 1) {
        setStep('กำลังสร้างรูป 1/1');
        setProgress(100);
        download(await toBlob(draw(0)), fileName(0));
        return true;
      }
      const used = new Set();
      const files = [];
      for (let i = 0; i < count; i += 1) {
        setStep(`กำลังสร้างรูป ${i + 1}/${count}`);
        setProgress(Math.round(((i + 1) / count) * 100));
        // eslint-disable-next-line no-await-in-loop
        const blob = await toBlob(draw(i));
        // eslint-disable-next-line no-await-in-loop
        const bytes = new Uint8Array(await blob.arrayBuffer());
        files.push({ name: uniqueName(fileName(i), used), data: bytes });
      }
      setStep(`กำลังรวมเป็นไฟล์ zip (${count} ไฟล์)…`);
      await new Promise((r) => setTimeout(r, 0)); // ให้ข้อความขึ้นก่อนเริ่มรวมไฟล์
      download(zipStore(files), zipName);
      return true;
    } catch (err) {
      onNotify('สร้างไฟล์ไม่สำเร็จ ลองอีกครั้ง', 'error');
      return false;
    } finally {
      setStep('');
      setProgress(-1);
      setBusy(false);
    }
  };

  /* ทีละใบ: ขนาดจริงของป้าย 300 dpi */
  const exportEach = async () => {
    const { w, h } = labelMm();
    const pxPerMm = 300 / 25.4;
    const cw = Math.round(w * pxPerMm);
    const ch = Math.round(h * pxPerMm);
    const n = data.items.length;
    const ok = await save(
      n,
      (i) => {
        const c = document.createElement('canvas');
        c.width = cw;
        c.height = ch;
        drawLabel(c.getContext('2d'), 0, 0, cw, ch, data.items[i], imgs[i]);
        return c;
      },
      (i) => `${code}_${data.items[i].sku}.png`,
      `${code}_PNG.zip`
    );
    if (ok) onNotify(n === 1 ? 'ส่งออก 1 ใบแล้ว' : `ส่งออก ${n} ใบ เป็นไฟล์ ZIP แล้ว`, 'success');
  };

  /* แผ่น A4 แนวนอน 200 dpi เรียงตามแม่แบบ (cols x rows) */
  const exportSheet = async () => {
    const t = data.template;
    const cols = t.paper === 'A4' ? t.cols : 4;
    const rows = t.paper === 'A4' ? t.rows : 4;
    const pxPerMm = 200 / 25.4;
    const pageW = Math.round(297 * pxPerMm);
    const pageH = Math.round(210 * pxPerMm);
    const lw = Math.round(70 * pxPerMm);
    const lh = Math.round(50 * pxPerMm);
    const gapX = (pageW - cols * lw) / (cols + 1);
    const gapY = (pageH - rows * lh) / (rows + 1);
    const perPage = cols * rows;
    const pages = Math.ceil(data.items.length / perPage);
    const ok = await save(
      pages,
      (p) => {
        const c = document.createElement('canvas');
        c.width = pageW;
        c.height = pageH;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageW, pageH);
        data.items.slice(p * perPage, (p + 1) * perPage).forEach((it, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          drawLabel(
            ctx,
            gapX + col * (lw + gapX),
            gapY + row * (lh + gapY),
            lw,
            lh,
            it,
            imgs[p * perPage + i]
          );
        });
        return c;
      },
      (p) => `${code}_A4_${p + 1}.png`,
      `${code}_A4.zip`
    );
    if (ok)
      onNotify(
        pages === 1 ? 'ส่งออก 1 แผ่นแล้ว' : `ส่งออก ${pages} แผ่น เป็นไฟล์ ZIP แล้ว`,
        'success'
      );
  };

  return (
    <>
      <MDButton variant='gradient' color='success' onClick={load} disabled={disabled}>
        <Icon>image</Icon>&nbsp;PNG
      </MDButton>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>ส่งออกป้ายเป็นรูป PNG · {code}</DialogTitle>
        <DialogContent>
          <MDBox mt={1} textAlign='center' sx={{ minHeight: 210 }}>
            {preview ? (
              <img
                src={preview}
                alt='ตัวอย่างป้าย'
                style={{ width: 280, border: '1px solid #ddd' }}
                data-tag-preview='1'
              />
            ) : (
              <MDTypography variant='caption' color='text'>
                กำลังโหลดข้อมูลป้าย…
              </MDTypography>
            )}
            {data ? (
              <MDTypography variant='caption' color='text' display='block' mt={1}>
                ตัวอย่างใบแรก · ทั้งหมด {data.items.length} ใบ
              </MDTypography>
            ) : null}
          </MDBox>
          {progress >= 0 ? (
            <MDBox mt={2}>
              <MDProgress value={progress} />
              <MDTypography
                variant='caption'
                color='text'
                display='block'
                mt={0.5}
                aria-live='polite'
              >
                {step}
              </MDTypography>
            </MDBox>
          ) : null}
          <MDBox mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDButton
                  variant='outlined'
                  color='dark'
                  fullWidth
                  onClick={exportEach}
                  disabled={busy || !data || !data.items.length}
                >
                  <Icon>filter_none</Icon>&nbsp;ทีละใบ {labelMm().w}×{labelMm().h} มม.
                </MDButton>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDButton
                  variant='outlined'
                  color='dark'
                  fullWidth
                  onClick={exportSheet}
                  disabled={busy || !data || !data.items.length}
                >
                  <Icon>grid_on</Icon>&nbsp;แผ่น A4 รวม
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='text' color='error' onClick={() => setOpen(false)} disabled={busy}>
            ปิด
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

TagExportPng.propTypes = {
  tagId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  code: PropTypes.string.isRequired,
  app: PropTypes.shape({
    token: PropTypes.string,
    lang: PropTypes.string,
    perm: PropTypes.objectOf(PropTypes.bool),
  }).isRequired,
  onNotify: PropTypes.func.isRequired,
  confirmExpired: PropTypes.func,
  disabled: PropTypes.bool,
  deniedText: PropTypes.string,
};

/* 'Y' = ผู้ใช้ยืนยันพิมพ์แคมเปญนอกช่วงวันที่ · 'N' = ไม่ต้องถาม · null = ยกเลิก (Tag.jsx ส่งมา)
   deniedText = เหตุผลเมื่อ server ไม่ให้ส่งออก เช่น "แคมเปญปิดใช้งาน" (18 ต.ค. 2569) */
TagExportPng.defaultProps = {
  confirmExpired: () => Promise.resolve('N'),
  disabled: false,
  deniedText: '',
};

export default TagExportPng;
